import { DialogProps } from '@lumapps/lumx/react';
import { NavigationMapItem, NavigationItemType } from '@lumapps/navigation/types';
import { GlobalPickerOptionProps } from '@lumapps/pickers/components/GlobalPickerOption';

import type { NavigationItemModalProps } from './components/NavigationItemModal/NavigationItemModal';

export type NavigationItemTypeWizardOption =
    | NavigationItemType.COMMUNITY
    | NavigationItemType.SECTION
    | NavigationItemType.CONTENT
    | NavigationItemType.DIRECTORY
    | NavigationItemType.CUSTOM_LIST
    | NavigationItemType.USER_DIRECTORY
    | NavigationItemType.ARTICLE
    | NavigationItemType.LINK
    | NavigationItemType.JOURNEYS
    | NavigationItemType.PERSONAL_FEED
    | NavigationItemType.VIDEO_GALLERY;

export type AvailableNavigationWizardOptionsType = Record<NavigationItemTypeWizardOption, boolean | null>;

export const DEFAULT_ENABLED_OPTIONS: AvailableNavigationWizardOptionsType = {
    [NavigationItemType.SECTION]: true,
    [NavigationItemType.LINK]: true,
    [NavigationItemType.JOURNEYS]: null,
    [NavigationItemType.PERSONAL_FEED]: null,
    [NavigationItemType.VIDEO_GALLERY]: null,
    [NavigationItemType.ARTICLE]: null,
    [NavigationItemType.CONTENT]: true,
    [NavigationItemType.CUSTOM_LIST]: null,
    [NavigationItemType.COMMUNITY]: true,
    [NavigationItemType.DIRECTORY]: null,
    [NavigationItemType.USER_DIRECTORY]: null,
};

export interface NavigationItemModal {
    /** default language for the platform */
    defaultLanguage: string;
    /** list of languages for the platform */
    languages: string[];
    /** callback to be executed once the navigation is created/added */
    onSave: (navItem: NavigationMapItem) => void;
    /** navigation item to edit */
    navigationItem: NavigationMapItem;
    /** whether the nav item modal is open or not */
    isOpen?: boolean;
    /** reference to the element that has triggered the modal */
    buttonRef?: DialogProps['parentElement'];
    /** callback to be executed once the modal is closed */
    onClose: () => void;
    /** whether we are editing the navigation item or not */
    isEdit?: boolean;
    /** scope for tracking purposes */
    scope: string;
    /** props to be passed into the navigation item modal component */
    navigationItemModalProps?: Partial<NavigationItemModalProps>;
}

export interface NavigationItemWizardOptionProps {
    /** Whether the data are fetched at instance or customer level */
    fetchAtInstanceLevel?: boolean;
    /** default language of the current user */
    defaultLanguage: string;
    /** list of available languages */
    languages: string[];
    /** callback to be executed once the navigation is created/added */
    onAdded: (navItem: NavigationMapItem) => void;
    /** callback that displays/hides the wizard */
    toggleWizard: (isOpen: boolean) => void;
    /**
     * callback to be executed once a wizard options is selected. This prevents
     * the wizard from closing, so make sure to call this function once an option is chosen!
     */
    setWasOptionSelected: (wasOptionSelected: boolean) => void;
    /** determines which options should be displayed and which ones should not */
    options?: AvailableNavigationWizardOptionsType;
    /** controls whether the navigation item modal for this open is already open or not */
    isOpen?: boolean;
    /** scope for tracking purposes */
    scope: string;
    /** whether the option is disabled or not */
    isDisabled?: boolean;
    /** props to be passed into the option component */
    optionProps?: Partial<GlobalPickerOptionProps>;
    /** props to be passed into the navigation item modal component */
    navigationItemModalProps?: Partial<NavigationItemModalProps>;
}

export type AvailableFilters = 'group' | 'language' | 'hideFiltered';

export enum AvailableNavigationItemFields {
    title = 'title',
    nodeTitle = 'nodeTitle',
    url = 'url',
    visibility = 'visibility',
    icon = 'icon',
    openInNewTab = 'openInNewTab',
    displayOnlyInLanguage = 'displayOnlyInLanguage',
    useAsSubNavigation = 'useAsSubNavigation',
    customCode = 'customCode',
}

/** Object of all available fields set at true */
export const ALL_AVAILABLE_FIELDS_OBJECT = Object.values(AvailableNavigationItemFields).reduce(
    (acc, curr) => ({ ...acc, [curr]: true }),
    {},
);

export const AVAILABLE_FIELDS_IN_MORE_SECTION = [
    AvailableNavigationItemFields.icon,
    AvailableNavigationItemFields.openInNewTab,
    AvailableNavigationItemFields.displayOnlyInLanguage,
    AvailableNavigationItemFields.useAsSubNavigation,
    AvailableNavigationItemFields.customCode,
];

/**
 * The configuration on the navigation admin.
 * Contains the information of what should or should not be displayed
 */
export interface NavigationAdminConfiguration {
    /** What filter should be available to use */
    availableFilters?: { [key in AvailableFilters]?: boolean };
    /** What field can be edited */
    availableEditableNodeFields?: { [key in AvailableNavigationItemFields]?: boolean };
    /** callback that determines whether a node is a list-type node. Defaults to isSectionNode() */
    isListNode?(node: NavigationMapItem): boolean;
    /** Whether the navigation should open the empty sections on load. Defaults to true */
    shouldOpenEmptySectionsOnInit?: boolean;
    /** Whether the navigation is at instance level or customer level. Default to instance */
    fetchAtInstanceLevel?: boolean;
    /** Whether external urls should be validated */
    validateExternalUrl?: boolean;
}
